import Input from "@/app/components/ui/Inputs/Input";
import InputWithFileUpload from "@/app/components/ui/Inputs/InputWithFileUpload";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useUtils } from "@/app/hooks/useUtils";
import { has } from "lodash";
import React from "react";
import { UseFormReturn } from "react-hook-form";
import { IAddVendorPanDetailsFormFields } from "..";

interface VendorPanDetailsFormComponentProps {
  panDetailsFormHook: UseFormReturn<IAddVendorPanDetailsFormFields>;
  isForEdit: boolean;
}

const VendorPanDetailsFormComponent: React.FC<VendorPanDetailsFormComponentProps> = ({
  panDetailsFormHook,
  isForEdit,
}) => {
  const { forAlphaNumericWithoutSpace } = useFormValidations();
  const { base64ToValidURL } = useUtils();
  return (
    <form onSubmit={panDetailsFormHook?.handleSubmit(() => {})} className="bg-white mt-4 py-4 rounded-md">
      <p className="font-semibold text-md">Pan Details</p>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
        <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
          <span className="font-normal text-black text-xs">PAN Number</span>
          <span className="font-normal text-rose-600 text-xs">*</span>
        </div>
        <Input
          maxLength={10}
          errorMessages={[
            forAlphaNumericWithoutSpace.errors,
            { message: "PAN Number is required", type: "required" },
            { message: "Max 10", type: "maxLength" },
          ]}
          fieldError={panDetailsFormHook.formState.errors.pan_number}
          placeholder="Enter PAN Number"
          register={panDetailsFormHook.register("pan_number", {
            required: true,
            // onBlur: () => panDetailsFormHook.trigger(`pan_number`),
            maxLength: 10,
            ...forAlphaNumericWithoutSpace.validations,
            onChange: () => panDetailsFormHook?.trigger("pan_number"),
          })}
          className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
          inputClassName="h-[38px] rounded-md"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            panDetailsFormHook?.setValue("pan_number", e.target?.value);
            panDetailsFormHook.clearErrors("pan_number");
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
        <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
          <span className="font-normal text-black text-xs">PAN Attachment</span>
        </div>
        <div className="w-full sm:w-2/6 lg:w-1/4">
          <InputWithFileUpload
            variant="DRAG_AND_DROP"
            fieldError={panDetailsFormHook.formState.errors?.pan_attachment_file ?? {}}
            register={panDetailsFormHook?.register("pan_attachment_file", {
              required: false,
              onChange: () => panDetailsFormHook.trigger(`pan_attachment_file`),
              validate: {
                maxSize: (files: FileList) => {
                  return (
                    Array.from(files).every((file) => file.size < 5 * 1024 * 1024) ||
                    "Each file must be smaller than 5MB"
                  );
                },
                fileType: (files: FileList) => {
                  const validTypes = ["image/png", "image/jpeg", "application/pdf"];
                  return Array.from(files).every((file) => validTypes.includes(file.type)) || "Invalid file type";
                },
              },
            })}
            type="file"
            onClear={() => {
              panDetailsFormHook.resetField("pan_attachment_file");
            }}
            className="border-slate-400 rounded-md w-full sm:w-[300px]"
            id={"pan_attachment_file"}
            files={panDetailsFormHook.watch("pan_attachment_file")}
            errorMessages={[{ type: "required", message: "PAN attachment is required." }]}
            dragAndDropDescription={{ allowedFormats: ["PDF", "JPEG", "PNG"], maxFileSize: "5MB" }}
          />
          <div className="flex items-center gap-2 my-3">
            {panDetailsFormHook?.watch("pan_attachment") &&
            has(panDetailsFormHook?.watch("pan_attachment"), "base_64_string") ? (
              <img
                src={base64ToValidURL(
                  panDetailsFormHook?.watch("pan_attachment").base_64_string,
                  panDetailsFormHook?.watch("pan_attachment").file_extension,
                )}
                alt=""
                className="w-full sm:w-1/2 h-full"
              />
            ) : (
              <img
                src={panDetailsFormHook?.watch("pan_attachment")?.document_url}
                alt=""
                className="w-full sm:w-1/2 h-full"
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
        <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
          <span className="font-normal text-black text-xs">PAN Operational Certification</span>
        </div>
        <div className="w-full sm:w-2/6 lg:w-1/4">
          <InputWithFileUpload
            variant="DRAG_AND_DROP"
            fieldError={panDetailsFormHook.formState.errors?.pan_operational_certificate_attachment_file ?? {}}
            register={panDetailsFormHook?.register("pan_operational_certificate_attachment_file", {
              required: false,
              onChange: () => panDetailsFormHook.trigger(`pan_operational_certificate_attachment_file`),
              validate: {
                maxSize: (files: FileList) => {
                  return (
                    Array.from(files).every((file) => file.size < 5 * 1024 * 1024) ||
                    "Each file must be smaller than 5MB"
                  );
                },
                fileType: (files: FileList) => {
                  const validTypes = ["image/png", "image/jpeg", "application/pdf"];
                  return Array.from(files).every((file) => validTypes.includes(file.type)) || "Invalid file type";
                },
              },
            })}
            onClear={() => {
              panDetailsFormHook.resetField("pan_operational_certificate_attachment_file");
            }}
            type="file"
            files={panDetailsFormHook.watch("pan_operational_certificate_attachment_file")}
            className="border-slate-400 rounded-md w-full sm:w-[300px]"
            id="pan_operational_certificate_attachment_file"
            errorMessages={[{ type: "required", message: "Media Required" }]}
            dragAndDropDescription={{ allowedFormats: ["PDF", "JPEG", "PNG"], maxFileSize: "5MB" }}
          />
          <div className="flex items-center gap-2 my-3">
            {panDetailsFormHook?.watch("pan_operational_certificate_attachment") &&
            has(panDetailsFormHook?.watch("pan_operational_certificate_attachment"), "base_64_string") ? (
              <img
                src={base64ToValidURL(
                  panDetailsFormHook?.watch("pan_operational_certificate_attachment").base_64_string,
                  panDetailsFormHook?.watch("pan_operational_certificate_attachment").file_extension,
                )}
                alt=""
                className="w-full sm:w-1/2 h-full"
              />
            ) : (
              <img
                src={panDetailsFormHook?.watch("pan_operational_certificate_attachment")?.document_url}
                alt=""
                className="w-full sm:w-1/2 h-full"
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
        <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
          <span className="font-normal text-black text-xs">Compliance as per 194Q Attachment</span>
        </div>
        <div className="w-full sm:w-2/6 lg:w-1/4">
          <InputWithFileUpload
            variant="DRAG_AND_DROP"
            fieldError={panDetailsFormHook.formState.errors?.compliance_194Q_attachment_file ?? {}}
            register={panDetailsFormHook?.register("compliance_194Q_attachment_file", {
              required: false,
              onChange: () => panDetailsFormHook.trigger(`compliance_194Q_attachment_file`),
              validate: {
                maxSize: (files: FileList) => {
                  return (
                    Array.from(files).every((file) => file.size < 5 * 1024 * 1024) ||
                    "Each file must be smaller than 5MB"
                  );
                },
                fileType: (files: FileList) => {
                  const validTypes = ["image/png", "image/jpeg", "application/pdf"];
                  return Array.from(files).every((file) => validTypes.includes(file.type)) || "Invalid file type";
                },
              },
            })}
            type="file"
            onClear={() => {
              panDetailsFormHook.resetField("compliance_194Q_attachment_file");
            }}
            files={panDetailsFormHook.watch("compliance_194Q_attachment_file")}
            className="border-slate-400 rounded-md w-full sm:w-[300px]"
            id="compliance_194Q_attachment_file"
            errorMessages={[{ type: "required", message: "Media Required" }]}
            dragAndDropDescription={{ allowedFormats: ["PDF", "JPEG", "PNG"], maxFileSize: "5MB" }}
          />
          <div className="flex items-center gap-2 my-3">
            {panDetailsFormHook?.watch("compliance_194Q_attachment") &&
            has(panDetailsFormHook?.watch("compliance_194Q_attachment"), "base_64_string") ? (
              <img
                src={base64ToValidURL(
                  panDetailsFormHook?.watch("compliance_194Q_attachment").base_64_string,
                  panDetailsFormHook?.watch("compliance_194Q_attachment").file_extension,
                )}
                alt=""
                className="w-full sm:w-1/2 h-full"
              />
            ) : (
              <img
                src={panDetailsFormHook?.watch("compliance_194Q_attachment")?.document_url}
                alt=""
                className="w-full sm:w-1/2 h-full"
              />
            )}
          </div>
        </div>
      </div>
    </form>
  );
};

export default VendorPanDetailsFormComponent;
